import {
  Box, Table, Thead, Tbody, Tr, Th, Td, IconButton, Input, Text, Badge,
  HStack, Flex, useToast, Tabs, TabList, Tab, TabPanels, TabPanel, Select, Spacer,
} from '@chakra-ui/react';
import TableSkeleton from '../../components/TableSkeleton';
import { useEffect, useMemo, useState } from 'react';
import { MdGavel, MdVisibility, MdAutoAwesome, MdPictureAsPdf } from 'react-icons/md';
import juzgadoApi from '../../services/juzgadoApi';
import ModalCalificarInfraccion from './ModalCalificarInfraccion';

export default function TablaInfraccionesPendientes() {
  const [pendientes, setPendientes] = useState<any[]>([]);
  const [todas, setTodas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [infraccionSel, setInfraccionSel] = useState<any>(null);
  const toast = useToast();

  const norm = (v?: string | null) => (v || '').toLowerCase();

  const fetchData = async () => {
    try {
      setLoading(true);
      const [pend, all] = await Promise.all([
        juzgadoApi.listarPendientesCalificar(),
        juzgadoApi.listarInfracciones(),
      ]);
      setPendientes(pend);
      setTodas(all);
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filtrarLista = (lista: any[]) => {
    const f = norm(filtro);
    if (!f) return lista;
    return lista.filter(m =>
      norm(m.folio).includes(f) ||
      norm(m.ciudadano).includes(f) ||
      norm(m.nombreOficial).includes(f)
    );
  };

  const colorEstatus = (e?: string) => {
    const s = (e || '').toLowerCase();
    if (s === 'pagado') return 'green';
    if (s === 'sancionada') return 'purple';
    if (s === 'pendiente') return 'orange';
    if (s === 'cancelada') return 'gray';
    return 'blue';
  };

  const TablaInfracciones = ({ lista }: { lista: any[] }) => (
    <Box bg="white" borderRadius="lg" shadow="sm" overflowX="auto">
      <Table variant="simple" size="sm">
        <Thead bg="#5b182b">
          <Tr>
            <Th color="white">Folio</Th>
            <Th color="white">Ciudadano</Th>
            <Th color="white">Fecha</Th>
            <Th color="white">Lugar</Th>
            <Th color="white">Oficial</Th>
            <Th color="white">Monto</Th>
            <Th color="white">Estatus</Th>
            <Th color="white">Acciones</Th>
          </Tr>
        </Thead>
        <Tbody>
          {lista.length === 0 && (
            <Tr><Td colSpan={8} textAlign="center" color="gray.400" py={8}>Sin infracciones</Td></Tr>
          )}
          {lista.map(m => (
            <Tr key={m.id} _hover={{ bg: 'gray.50' }}>
              <Td fontWeight="medium" color="#5b182b">{m.folio}</Td>
              <Td>{m.ciudadano ?? '—'}</Td>
              <Td whiteSpace="nowrap" fontSize="sm">{m.fechaInfraccion ? new Date(m.fechaInfraccion).toLocaleDateString('es-MX') : '—'}</Td>
              <Td fontSize="sm" maxW="150px" isTruncated>{m.lugarInfraccion ?? '—'}</Td>
              <Td fontSize="sm">{m.nombreOficial ?? '—'}</Td>
              <Td isNumeric>${Number(m.monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</Td>
              <Td>
                <Badge colorScheme={colorEstatus(m.estatus)} px={2} borderRadius="full">
                  {m.estatus || 'PENDIENTE'}
                </Badge>
              </Td>
              <Td>
                <HStack spacing={1}>
                  <IconButton
                    aria-label="Calificar"
                    icon={<MdGavel />}
                    size="sm"
                    bg="#5b182b"
                    color="white"
                    _hover={{ bg: '#691f30' }}
                    onClick={() => { setInfraccionSel(m); setModalOpen(true); }}
                    title="Clasificar y calificar"
                  />
                  <IconButton
                    aria-label="Sugerir IA"
                    icon={<MdAutoAwesome />}
                    size="sm"
                    colorScheme="purple"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const sug = await juzgadoApi.sugerirClasificacionIA(m.id);
                        toast({ title: `IA: ${sug.confianza ?? 'BAJA'}`, description: sug.articuloSugerido ?? sug.mensaje, status: 'info', duration: 6000 });
                        await fetchData();
                      } catch (e: any) {
                        toast({ title: 'Error IA', description: e.message, status: 'error' });
                      }
                    }}
                    title="Sugerir clasificación con IA"
                  />
                  <IconButton
                    aria-label="Boleta PDF"
                    icon={<MdPictureAsPdf />}
                    size="sm"
                    colorScheme="red"
                    variant="outline"
                    as="a"
                    href={juzgadoApi.pdfBoletaInfraccion(m.id)}
                    target="_blank"
                    title="Descargar boleta de infracción"
                  />
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );

  return (
    <Box p={4} pt="79px" position="relative">
      <Text fontSize="2xl" fontWeight="bold" mb={4} color="#5b182b">
        Infracciones de Tránsito
      </Text>

      <Flex mb={4} gap={2} wrap="wrap">
        <Input
          placeholder="Buscar por folio, ciudadano, oficial..."
          value={filtro}
          onChange={e => setFiltro(e.target.value)}
          maxW="340px"
          borderColor="#bb8c52"
          _focus={{ borderColor: '#5b182b', boxShadow: '0 0 0 1px #5b182b' }}
        />
        <Spacer />
      </Flex>

      {loading ? (
        <Box bg="white" borderRadius="lg" shadow="sm" overflow="hidden">
          <TableSkeleton rows={8} columns={8} />
        </Box>
      ) : (
        <Tabs colorScheme="red" variant="enclosed">
          <TabList>
            <Tab _selected={{ color: '#5b182b', borderColor: '#5b182b' }}>
              Pendientes de calificar
              <Badge ml={2} colorScheme="orange">{pendientes.length}</Badge>
            </Tab>
            <Tab _selected={{ color: '#5b182b', borderColor: '#5b182b' }}>
              Todas las infracciones
              <Badge ml={2} colorScheme="gray">{todas.length}</Badge>
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0}>
              <TablaInfracciones lista={filtrarLista(pendientes)} />
            </TabPanel>
            <TabPanel px={0}>
              <TablaInfracciones lista={filtrarLista(todas)} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}

      <ModalCalificarInfraccion
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        infraccion={infraccionSel}
        onGuardado={() => { fetchData(); setModalOpen(false); }}
      />
    </Box>
  );
}
